import React, { useState } from 'react';
import { FaChevronDown, FaArrowUpRightFromSquare } from 'react-icons/fa6';
import { COURSE_REFERENCES, COURSE_REFERENCES_INTRO } from '../data/courseReferences';

// References lesson body. Rendered by courseLesson.js when the user lands on
// the references slug inside the Course Complete module (added after the
// thank-you lesson and just before the certificate page).
//
// Native <details>/<summary> would also work, but we use a controlled
// useState so the chevron icon and accordion stay perfectly in sync with the
// rest of the lesson page chevrons (FaChevronDown rotates 180deg when open).
//
// Module 1 is open by default; the other five collapse so the page is
// scannable on first load.
export default function CourseReferences() {
  const [openIdx, setOpenIdx] = useState(() => new Set([0]));

  const toggle = (i) =>
    setOpenIdx((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  return (
    <div>
      {/* Intro paragraph, kept verbatim to match the source document. */}
      <p className="text-base sm:text-lg text-ink leading-relaxed">
        {COURSE_REFERENCES_INTRO}
      </p>

      {/* Accordion: one section per module, with reference count in summary. */}
      <div className="mt-10 divide-y divide-ink/10 border-y border-ink/10">
        {COURSE_REFERENCES.map((group, i) => {
          const isOpen = openIdx.has(i);
          const panelId = `course-references-panel-${i}`;
          const buttonId = `course-references-button-${i}`;
          return (
            <section key={group.module}>
              <button
                id={buttonId}
                type="button"
                onClick={() => toggle(i)}
                aria-expanded={isOpen}
                aria-controls={panelId}
                className="w-full flex items-start sm:items-center justify-between gap-4 py-5 text-left group"
              >
                <span className="font-display text-lg sm:text-xl text-brand-deep leading-snug">
                  {group.module}
                  <span className="ml-2 text-sm font-sans font-normal text-slate2">
                    ({group.refs.length})
                  </span>
                </span>
                <FaChevronDown
                  className={`flex-shrink-0 mt-1 sm:mt-0 text-sm text-brand-deep/70 transition-transform ${
                    isOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {isOpen && (
                <ul
                  id={panelId}
                  role="region"
                  aria-labelledby={buttonId}
                  className="pb-6 space-y-3"
                >
                  {group.refs.map((ref) => (
                    <li key={ref.url} className="leading-relaxed">
                      <a
                        href={ref.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-brand-deep underline underline-offset-2 hover:text-ink"
                      >
                        {ref.title}
                      </a>
                      <span className="text-slate2"> &middot; {ref.source}</span>
                      <FaArrowUpRightFromSquare
                        aria-hidden="true"
                        className="inline-block ml-1.5 text-[10px] text-slate2/60 align-baseline"
                      />
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
